import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HelpCircle, MessageSquare, Image as ImageIcon } from 'lucide-react';

interface FloatingActionClusterProps {
  onOpenChat: () => void;
  onOpenFeedback: () => void;
  onOpenGallery: () => void;
  unreadFeedbackCount: number;
  totalImageCount: number;
  hasConversation: boolean;
  hasActiveJobs: boolean;
  hasAccessToAiChat: boolean;
  showGallery: boolean;
  isChatOpen: boolean;
  isFeedbackOpen: boolean;
}

interface ClusterButton {
  id: string;
  icon: React.ReactNode;
  gradient: string;
  label: string;
  onClick: () => void;
  badge: React.ReactNode | null;
}

// Arc positions for up to 3 buttons, measured from anchor (bottom-right).
// Radius 120px, angles -30°, -75°, -120° for even spacing without overlap.
const ARC_POSITIONS = [
  { x: -60, y: -104 },  // -30° from right (mostly up, slightly left)
  { x: -104, y: -60 },  // -75° (diagonal)
  { x: -120, y: 0 },    // -120° (mostly left)
];

const ARC_POSITIONS_2 = [
  { x: -60, y: -104 },
  { x: -120, y: 0 },
];

// Collapsed offsets so buttons peek out behind the front one
const COLLAPSED_OFFSETS = [
  { x: 0, y: 0 },      // front button — anchor position
  { x: -6, y: -6 },    // second — peeking top-left
  { x: -12, y: -12 },  // third — further top-left
];

const FloatingActionCluster: React.FC<FloatingActionClusterProps> = ({
  onOpenChat,
  onOpenFeedback,
  onOpenGallery,
  unreadFeedbackCount,
  totalImageCount,
  hasConversation,
  hasActiveJobs,
  hasAccessToAiChat,
  showGallery,
  isChatOpen,
  isFeedbackOpen,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveredRef = useRef(false);

  // All hooks must be called before any early returns (React rules of hooks)

  // Close on outside click (touch support)
  useEffect(() => {
    if (!isExpanded) return;
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isExpanded]);

  // Auto-collapse after 3s of no interaction (touch devices only — not while hovering)
  const resetCollapseTimer = useCallback(() => {
    if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = setTimeout(() => setIsExpanded(false), 3000);
  }, []);

  useEffect(() => {
    if (isExpanded && !isHoveredRef.current) resetCollapseTimer();
    return () => {
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    };
  }, [isExpanded, resetCollapseTimer]);

  // Build the list of visible buttons
  const buttons: ClusterButton[] = [];

  if (hasAccessToAiChat && !isChatOpen) {
    buttons.push({
      id: 'chat',
      icon: <HelpCircle className="w-6 h-6" />,
      gradient: 'from-purple-600 to-purple-500',
      label: 'Chat',
      onClick: onOpenChat,
      badge: hasConversation ? (
        <span className="absolute top-0 right-0 w-3 h-3 bg-purple-300 rounded-full border-2 border-purple-600" />
      ) : null,
    });
  }

  if (!isFeedbackOpen) {
    buttons.push({
      id: 'feedback',
      icon: <MessageSquare className="w-6 h-6" />,
      gradient: 'from-blue-600 to-blue-500',
      label: 'Feedback',
      onClick: onOpenFeedback,
      badge: unreadFeedbackCount > 0 ? (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
          {unreadFeedbackCount > 9 ? '9+' : unreadFeedbackCount}
        </span>
      ) : null,
    });
  }

  if (showGallery) {
    buttons.push({
      id: 'gallery',
      icon: <ImageIcon className="w-6 h-6" />,
      gradient: 'from-purple-600 to-purple-500',
      label: 'Gallery',
      onClick: onOpenGallery,
      badge: totalImageCount > 0 ? (
        <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs font-bold rounded-full min-w-6 h-6 px-1.5 flex items-center justify-center">
          {totalImageCount > 99 ? '99+' : totalImageCount}
        </span>
      ) : null,
    });
  }

  if (buttons.length === 0) return null;

  // Attention dot only for actionable badges (chat conversation, unread feedback) — not gallery count
  const hasAttentionBadge = (hasConversation && hasAccessToAiChat && !isChatOpen) || unreadFeedbackCount > 0;
  const arcPositions = buttons.length === 2 ? ARC_POSITIONS_2 : ARC_POSITIONS;

  const handleMouseEnter = () => {
    isHoveredRef.current = true;
    if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;
    setIsExpanded(false);
  };

  const handleTap = () => {
    setIsExpanded((prevIsExpanded) => !prevIsExpanded);
  };

  const handleButtonClick = (button: ClusterButton) => {
    setIsExpanded(false);
    button.onClick();
  };

  // 1-button fallback: render as plain standalone button
  if (buttons.length === 1) {
    const btn = buttons[0];
    return (
      <div className={`fixed z-40 transition-all duration-300 right-6 ${hasActiveJobs ? 'bottom-24' : 'bottom-6'}`}>
        <button
          onClick={btn.onClick}
          className={`relative bg-gradient-to-br ${btn.gradient} text-white rounded-full p-4 shadow-2xl hover:shadow-lg transition-all duration-300 hover:scale-110 active:scale-95`}
          aria-label={btn.label}
        >
          {btn.icon}
          {btn.badge}
        </button>
      </div>
    );
  }

  // Multi-button cluster
  return (
    <div
      ref={containerRef}
      className={`fixed z-40 transition-all duration-300 right-6 ${hasActiveJobs ? 'bottom-24' : 'bottom-6'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleTap}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleTap();
        }
      }}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? 'Collapse floating action menu' : 'Expand floating action menu'}
      style={{ width: isExpanded ? '200px' : '72px', height: isExpanded ? '180px' : '72px' }}
    >
      {/* Attention dot — visible only when collapsed and some button has a badge */}
      {!isExpanded && hasAttentionBadge && (
        <span className="absolute top-0 right-0 z-50 w-3 h-3 bg-orange-500 rounded-full border-2 border-white shadow transition-opacity duration-200" />
      )}

      {buttons.map((btn, index) => {
        const pos = arcPositions[index] || { x: 0, y: 0 };
        const delay = index * 50;

        return (
          <div
            key={btn.id}
            className="absolute bottom-0 right-0 transition-all ease-out"
            style={{
              transform: isExpanded
                ? `translate(${pos.x}px, ${pos.y}px) scale(1)`
                : `translate(${(COLLAPSED_OFFSETS[index] || { x: 0, y: 0 }).x}px, ${(COLLAPSED_OFFSETS[index] || { x: 0, y: 0 }).y}px) scale(${1 - index * 0.06})`,
              opacity: isExpanded ? 1 : (1 - index * 0.12),
              transitionDuration: '300ms',
              transitionDelay: isExpanded ? `${delay}ms` : '0ms',
              zIndex: isExpanded ? 10 : (buttons.length - index),
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleButtonClick(btn);
              }}
              className={`relative bg-gradient-to-br ${btn.gradient} text-white rounded-full p-4 shadow-2xl transition-all duration-200 ${
                isExpanded ? 'hover:scale-110 active:scale-95 cursor-pointer' : 'pointer-events-none'
              }`}
              aria-label={btn.label}
              tabIndex={isExpanded ? 0 : -1}
            >
              {btn.icon}
              {/* Individual badge — only visible when expanded */}
              {isExpanded && btn.badge && (
                <span className="transition-opacity duration-200">
                  {btn.badge}
                </span>
              )}
            </button>
            {/* Tooltip label — only when expanded */}
            {isExpanded && (
              <span
                className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none transition-opacity duration-200"
                style={{ opacity: isExpanded ? 0.9 : 0 }}
              >
                {btn.label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FloatingActionCluster;
